/**
 * Token 统计的数据请求（TanStack Query）。接口、参数与旧版 TokenUsageViewSimple.vue 一致：
 * 全局统计、用户排行（top 20）、模型排行（top 10）按所选日期范围请求；
 * 使用趋势只传天数（结束日期减开始日期，向上取整，为 0 时取 7）。
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { tokenUsageApi, type UsageDetailItem, type UsageDetailsParams } from '@/api/tokenUsage'

const TOKEN_USAGE_KEY = ['admin', 'token-usage'] as const

export interface DateRangeParams {
  start_date: string
  end_date: string
}

export interface GlobalSummary {
  total_requests?: number
  total_tokens?: number
  total_cost?: number
  active_users?: number
}

export interface ProviderCost {
  provider: string
  requests: number
  tokens: number
  cost: number
}

export interface UserRankingRow {
  user_id: number
  username: string
  total_requests: number
  total_tokens: number
  total_cost: number
}

export interface ModelRankingRow {
  provider: string
  model: string
  requests: number
  tokens: number
  cost: number
}

export interface TrendPoint {
  date: string
  requests: number
  tokens: number
  cost: number
}

// 接口返回 { success, data } 包装格式时取 data
function unwrap(responseData: any): any {
  return responseData?.success ? responseData.data : responseData
}

export function toDateRangeParams([start, end]: [Dayjs, Dayjs]): DateRangeParams {
  return { start_date: start.format('YYYY-MM-DD'), end_date: end.format('YYYY-MM-DD') }
}

export function trendDays([start, end]: [Dayjs, Dayjs]): number {
  return Math.ceil(end.startOf('day').diff(start.startOf('day'), 'day')) || 7
}

// 全局统计：卡片汇总，以及按供应商拆分的成本（by_provider 对象转数组）
export function parseGlobalStats(responseData: any): { summary: GlobalSummary; providers: ProviderCost[] } {
  const stats = unwrap(responseData) ?? {}
  const byProvider: Record<string, Omit<ProviderCost, 'provider'>> = stats.by_provider ?? {}
  return {
    summary: stats.summary ?? {},
    providers: Object.entries(byProvider).map(([provider, data]) => ({ ...data, provider }))
  }
}

// 用户排行：后端返回 { ranking: [...] }；旧版只认数组、items、results，排行一直是空的
export function parseUserRanking(responseData: any): UserRankingRow[] {
  const data = unwrap(responseData)
  if (Array.isArray(data)) return data
  return data?.ranking || data?.items || data?.results || []
}

export function parseModelRanking(responseData: any): ModelRankingRow[] {
  const data = unwrap(responseData)
  if (Array.isArray(data?.ranking)) return data.ranking
  return Array.isArray(data) ? data : []
}

export function parseTrend(responseData: any): TrendPoint[] {
  const data = unwrap(responseData)
  return Array.isArray(data?.trend) ? data.trend : []
}

export function useTokenStats(range: DateRangeParams, days: number) {
  const global = useQuery({
    queryKey: [...TOKEN_USAGE_KEY, 'global', range],
    queryFn: async () => parseGlobalStats((await tokenUsageApi.getGlobalStats(range)).data)
  })
  const userRanking = useQuery({
    queryKey: [...TOKEN_USAGE_KEY, 'user-ranking', range],
    queryFn: async () => parseUserRanking((await tokenUsageApi.getUserRanking({ ...range, top: 20 })).data)
  })
  const modelRanking = useQuery({
    queryKey: [...TOKEN_USAGE_KEY, 'model-ranking', range],
    queryFn: async () => parseModelRanking((await tokenUsageApi.getModelRanking({ ...range, top: 10 })).data)
  })
  const trend = useQuery({
    queryKey: [...TOKEN_USAGE_KEY, 'daily-trend', days],
    queryFn: async () => parseTrend((await tokenUsageApi.getDailyTrend(days)).data)
  })
  return { global, userRanking, modelRanking, trend }
}

export function useUsageDetails(params: UsageDetailsParams, enabled: boolean) {
  return useQuery({
    queryKey: [...TOKEN_USAGE_KEY, 'details', params],
    enabled,
    queryFn: async () => {
      const data = unwrap((await tokenUsageApi.getUsageDetails(params)).data)
      return { items: (data?.items ?? []) as UsageDetailItem[], total: (data?.pagination?.total ?? 0) as number }
    },
    placeholderData: keepPreviousData
  })
}
